// monkey referrals widget - phase 2
// embeddable script that customers paste on their sites

type mr_command = {
  command: string;
  args: any[];
};

interface widget_config {
  business_id: string;
  embed_code_id: string;
  form_selector?: string;
  insert_before_selector?: string;
  validate_on_blur: boolean;
  api_base_url: string;
}

interface reward_def {
  quantity: number;
  unit_type: string;
}

interface embed_rewards {
  new_client_reward?: reward_def;
  existing_client_reward?: reward_def;
}

interface widget_state {
  referral_code?: string;
  referral_token?: string;
  confirmation_token?: string;
  referral_share_code?: string;
  visitor_id: string;
  rewards?: embed_rewards;
}

const storage_keys = {
  referral_code: 'mr_referral_code',
  referral_code_session: 'mr_referral_code',
  confirmation_token: 'mr_confirmation_token',
  referral_share_code: 'mr_referral_share_code',
  visitor_id: 'mr_visitor_id',
  pending_submit: 'mr_pending_submit',
  pending_confirm: 'mr_pending_confirm',
};

// retry configuration
const retry_config = {
  max_retries: 3,
  base_delay_ms: 1000,
  max_delay_ms: 10000,
};

const default_api_base = 'https://monkeyreferrals.com';

let active_config: widget_config | null = null;
let widget_state: widget_state | null = null;
let mutation_observer: MutationObserver | null = null;
let bind_timeout_id: number | null = null;

function get_current_script(): HTMLScriptElement | null {
  if (document.currentScript) return document.currentScript as HTMLScriptElement;
  const scripts = Array.from(document.getElementsByTagName('script'));
  const matching = scripts.filter((script) => script.src && script.src.includes('mr.js'));
  return matching.length > 0 ? matching[matching.length - 1] : null;
}

function get_script_config(script_el: HTMLScriptElement): widget_config | null {
  const business_id = script_el.dataset.business || '';
  const embed_code_id = script_el.dataset.embed_code || script_el.dataset.embedCode || '';
  const form_selector = script_el.dataset.form || undefined;
  const insert_before_selector = script_el.dataset.insertBefore || undefined;
  const validate_on_blur = script_el.dataset.validate === '1' || script_el.dataset.validate === 'true';
  const api_base_url =
    script_el.dataset.apiBase ||
    get_script_origin(script_el) ||
    window.location.origin ||
    default_api_base;

  if (!business_id || !embed_code_id) {
    return null;
  }

  return {
    business_id,
    embed_code_id,
    form_selector,
    insert_before_selector,
    validate_on_blur,
    api_base_url: api_base_url.replace(/\/$/, ''),
  };
}

function get_script_origin(script_el: HTMLScriptElement): string | null {
  if (!script_el.src) return null;
  try {
    return new URL(script_el.src).origin;
  } catch {
    return null;
  }
}

function get_query_param(name: string): string | null {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(name);
  return value ? value.trim() : null;
}

function get_session_referral_code(): string | undefined {
  try {
    return sessionStorage.getItem(storage_keys.referral_code_session) || undefined;
  } catch {
    return undefined;
  }
}

function ensure_widget_state(): widget_state {
  if (widget_state) return widget_state;

  const visitor_id =
    localStorage.getItem(storage_keys.visitor_id) ||
    `visitor_${Math.random().toString(36).substring(2, 12)}`;

  localStorage.setItem(storage_keys.visitor_id, visitor_id);

  widget_state = {
    referral_code: get_session_referral_code() || localStorage.getItem(storage_keys.referral_code) || undefined,
    confirmation_token: localStorage.getItem(storage_keys.confirmation_token) || undefined,
    referral_share_code: localStorage.getItem(storage_keys.referral_share_code) || undefined,
    visitor_id,
  };

  return widget_state;
}

function set_referral_code(code: string | undefined) {
  const state = ensure_widget_state();
  if (code) {
    state.referral_code = code;
    localStorage.setItem(storage_keys.referral_code, code);
    try { sessionStorage.setItem(storage_keys.referral_code_session, code); } catch { /* ignore */ }
  } else {
    state.referral_code = undefined;
    localStorage.removeItem(storage_keys.referral_code);
    try { sessionStorage.removeItem(storage_keys.referral_code_session); } catch { /* ignore */ }
  }
}

function set_confirmation_token(token: string | undefined) {
  const state = ensure_widget_state();
  if (token) {
    state.confirmation_token = token;
    localStorage.setItem(storage_keys.confirmation_token, token);
  } else {
    state.confirmation_token = undefined;
    localStorage.removeItem(storage_keys.confirmation_token);
  }
}

function set_referral_share_code(code: string | undefined) {
  const state = ensure_widget_state();
  if (code) {
    state.referral_share_code = code;
    localStorage.setItem(storage_keys.referral_share_code, code);
  } else {
    state.referral_share_code = undefined;
    localStorage.removeItem(storage_keys.referral_share_code);
  }
}

function find_target_form(config: widget_config): HTMLFormElement | null {
  if (config.form_selector) {
    const form = document.querySelector(config.form_selector);
    return form instanceof HTMLFormElement ? form : null;
  }

  const forms = Array.from(document.getElementsByTagName('form'));
  for (const form of forms) {
    const submit_button = form.querySelector('button[type="submit"], input[type="submit"]');
    if (submit_button) return form;
  }

  return null;
}

function find_insert_before_element(form: HTMLFormElement, config: widget_config): Element | null {
  if (config.insert_before_selector) {
    return form.querySelector(config.insert_before_selector);
  }
  return form.querySelector('button[type="submit"], input[type="submit"]');
}

function create_container(): HTMLDivElement {
  const container = document.createElement('div');
  container.className = 'mr_widget_container';
  return container;
}

function inject_styles() {
  if (document.getElementById('mr_widget_styles')) return;
  const style = document.createElement('style');
  style.id = 'mr_widget_styles';
  style.textContent = `
    .mr_widget_container {
      box-sizing: border-box;
      min-width: 0;
      max-width: 100%;
      margin: 20px 0;
      padding: 16px;
      border: 1px solid var(--ttw_color_border, #e2e8f0);
      border-radius: var(--ttw_radius_lg, 16px);
      background: var(--ttw_color_surface, #fff);
      color: var(--ttw_color_text, #0f172a);
      font: 16px/1.6 var(--ttw_font_family, system-ui, sans-serif);
      text-align: left;
      overflow-wrap: anywhere;
    }
    .mr_widget_container *, .mr_widget_container *::before, .mr_widget_container *::after { box-sizing: border-box; }
    .mr_widget_container .mr_widget_label {
      display: block;
      margin: 0 0 8px;
      color: var(--ttw_color_text, #0f172a);
      font: 600 16px/1.5 var(--ttw_font_family, system-ui, sans-serif);
      cursor: pointer;
    }
    .mr_widget_container .mr_widget_input {
      display: block;
      width: 100%;
      min-width: 0;
      max-width: 100%;
      min-height: 48px;
      margin: 0;
      padding: 12px;
      border: 1px solid var(--ttw_color_border, #e2e8f0);
      border-radius: var(--ttw_radius, 8px);
      background: var(--ttw_color_surface, #fff);
      color: var(--ttw_color_text, #0f172a);
      font: 16px/1.5 var(--ttw_font_family, system-ui, sans-serif);
    }
    .mr_widget_container .mr_widget_input::placeholder { color: #64748b; opacity: 1; }
    .mr_widget_container .mr_widget_input:focus-visible,
    .mr_widget_container .mr_widget_button:focus-visible { outline: 2px solid var(--ttw_color_primary, #4f46e5); outline-offset: 3px; }
    .mr_widget_container .mr_widget_input[aria-invalid="true"] { border-color: #b91c1c; }
    .mr_widget_container .mr_widget_status { margin-top: 8px; color: var(--ttw_color_text_secondary, #475569); font: 14px/1.6 var(--ttw_font_family, system-ui, sans-serif); }
    .mr_widget_container .mr_widget_status:empty { position: absolute; width: 1px; height: 1px; margin: -1px; overflow: hidden; clip-path: inset(50%); }
    .mr_widget_container .mr_widget_status.success { color: #047857; }
    .mr_widget_container .mr_widget_status.error { color: #b91c1c; }
    .mr_widget_container .mr_widget_button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      max-width: 100%;
      min-height: 48px;
      margin-top: 12px;
      padding: 12px;
      background: var(--ttw_color_surface, #fff);
      color: var(--ttw_color_text, #0f172a);
      border: 1px solid var(--ttw_color_border, #e2e8f0);
      border-radius: var(--ttw_radius, 8px);
      font: 600 15px/1.5 var(--ttw_font_family, system-ui, sans-serif);
      white-space: normal;
      text-align: center;
      cursor: pointer;
    }
    .mr_widget_container .mr_widget_button:hover { background: var(--ttw_color_bg, #f8fafc); border-color: #475569; }
    dialog.mr_modal_overlay {
      box-sizing: border-box;
      width: 480px;
      max-width: calc(100vw - 32px);
      max-height: calc(100vh - 32px);
      max-height: calc(100dvh - 32px);
      margin: auto;
      padding: 0;
      border: 1px solid var(--ttw_color_border, #e2e8f0);
      border-radius: var(--ttw_radius_lg, 16px);
      background: var(--ttw_color_surface, #fff);
      color: var(--ttw_color_text, #0f172a);
      box-shadow: 0 24px 64px rgb(15 23 42 / 24%);
      overflow: auto;
    }
    dialog.mr_modal_overlay:not([open]) { display: none; }
    dialog.mr_modal_overlay::backdrop { background: rgb(15 23 42 / 55%); }
    .mr_modal_overlay *, .mr_modal_overlay *::before, .mr_modal_overlay *::after { box-sizing: border-box; }
    .mr_modal_overlay .mr_modal { min-width: 0; padding: 24px; font: 16px/1.6 var(--ttw_font_family, system-ui, sans-serif); text-align: left; overflow-wrap: anywhere; }
    .mr_modal_overlay .mr_modal_title { margin: 0; color: var(--ttw_color_text, #0f172a); font: 700 22px/1.3 var(--ttw_font_family, system-ui, sans-serif); }
    .mr_modal_overlay .mr_modal_label { display: block; margin: 20px 0 8px; font: 600 16px/1.5 var(--ttw_font_family, system-ui, sans-serif); }
    .mr_modal_overlay .mr_modal input {
      display: block;
      width: 100%;
      min-width: 0;
      max-width: 100%;
      min-height: 48px;
      margin: 0;
      padding: 12px;
      border: 1px solid var(--ttw_color_border, #e2e8f0);
      border-radius: var(--ttw_radius, 8px);
      background: var(--ttw_color_surface, #fff);
      color: var(--ttw_color_text, #0f172a);
      font: 16px/1.5 var(--ttw_font_family, system-ui, sans-serif);
    }
    .mr_modal_overlay .mr_modal input::placeholder { color: #64748b; opacity: 1; }
    .mr_modal_overlay .mr_modal input:focus-visible,
    .mr_modal_overlay .mr_modal button:focus-visible { outline: 2px solid var(--ttw_color_primary, #4f46e5); outline-offset: 3px; }
    .mr_modal_overlay .mr_modal_actions { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 12px; margin-top: 24px; }
    .mr_modal_overlay .mr_modal_button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 48px;
      max-width: 100%;
      padding: 12px 20px;
      border: 1px solid transparent;
      border-radius: var(--ttw_radius, 8px);
      background: var(--ttw_color_primary, #4f46e5);
      color: #fff;
      font: 600 15px/1.5 var(--ttw_font_family, system-ui, sans-serif);
      text-align: center;
      white-space: normal;
      cursor: pointer;
    }
    .mr_modal_overlay .mr_modal_button:hover:not(:disabled) { background: var(--ttw_color_primary_hover, #4338ca); }
    .mr_modal_overlay .mr_modal_button_secondary { background: var(--ttw_color_surface, #fff); color: var(--ttw_color_text, #0f172a); border-color: var(--ttw_color_border, #e2e8f0); }
    .mr_modal_overlay .mr_modal_button_secondary:hover:not(:disabled) { background: var(--ttw_color_bg, #f8fafc); border-color: #475569; }
    .mr_modal_overlay .mr_modal_button:disabled { opacity: .55; cursor: not-allowed; }
    .mr_modal_overlay .mr_modal_status { margin-top: 16px; color: var(--ttw_color_text_secondary, #475569); font-size: 14px; }
    .mr_modal_overlay .mr_modal_status:empty { position: absolute; width: 1px; height: 1px; margin: -1px; overflow: hidden; clip-path: inset(50%); }
    .mr_modal_overlay .mr_modal_status.success { color: #047857; }
    .mr_modal_overlay .mr_modal_status.error { color: #b91c1c; }
    .mr_modal_overlay .mr_modal_success { text-align: center; }
    .mr_modal_overlay .mr_modal_success_code { display: inline-block; max-width: 100%; overflow-wrap: anywhere; background: var(--ttw_color_success_light, #ecfdf5); border: 1px solid #a7f3d0; border-radius: 8px; padding: 12px 20px; margin: 16px 0; color: #065f46; font: 700 22px/1.6 ui-monospace, Consolas, monospace; letter-spacing: 2px; }
    .mr_modal_overlay .mr_modal_copy { margin: 12px 0 0; color: var(--ttw_color_text_secondary, #475569); font-size: 15px; }
    .mr_modal_overlay .mr_modal_rewards { margin-top: 20px; padding: 16px; background: var(--ttw_color_bg, #f8fafc); border: 1px solid var(--ttw_color_border, #e2e8f0); border-radius: 8px; font-size: 14px; text-align: left; }
    .mr_modal_overlay .mr_modal_rewards p { margin: 0; }
    .mr_modal_overlay .mr_modal_rewards p + p { margin-top: 4px; }
    .mr_modal_overlay .mr_modal_requirements { margin-top: 20px; color: var(--ttw_color_text_secondary, #475569); font-size: 14px; white-space: pre-wrap; text-align: left; }
    .mr_modal_overlay .mr_modal_requirements_label { font-weight: 600; margin-bottom: 4px; }
    .mr_modal_overlay .mr_modal_actions_center { justify-content: center; }
    .mr_widget_container .mr_widget_rewards {
      margin-top: 12px;
      padding: 12px;
      background: var(--ttw_color_success_light, #ecfdf5);
      border: 1px solid #a7f3d0;
      border-radius: var(--ttw_radius, 8px);
      color: var(--ttw_color_text_secondary, #475569);
      font: 14px/1.6 var(--ttw_font_family, system-ui, sans-serif);
    }
    .mr_widget_container .mr_widget_rewards p { margin: 0; }
    .mr_widget_container .mr_widget_rewards p + p { margin-top: 4px; }
  `;
  document.head.appendChild(style);
}

const MONEY_UNITS = new Set(['cash', 'gift card', 'store credit']);

/** "20" + "gift_card" -> "$20 Gift Card"; custom text such as "Extra Months Free" is kept as typed. */
function format_reward(r: reward_def): string {
  const unit = String(r.unit_type || '').replace(/_/g, ' ').trim();
  const label = unit.replace(/\b\w/g, (c) => c.toUpperCase());
  return MONEY_UNITS.has(unit.toLowerCase()) ? `$${r.quantity} ${label}` : `${r.quantity} ${label}`;
}

function create_modal(form_email_value: string, config: widget_config) {
  const overlay = document.createElement('dialog');
  overlay.className = 'mr_modal_overlay';
  const close_modal = () => {
    overlay.close();
    overlay.remove();
  };

  const modal = document.createElement('div');
  modal.className = 'mr_modal';

  const title = document.createElement('h2');
  title.id = create_widget_control_id('mr_modal_title');
  title.className = 'mr_modal_title';
  title.textContent = 'Get your referral code';
  overlay.setAttribute('aria-labelledby', title.id);

  const email_input = document.createElement('input');
  email_input.type = 'email';
  email_input.placeholder = 'Email *';
  email_input.value = form_email_value || '';
  email_input.required = true;
  email_input.id = create_widget_control_id('mr_modal_email');
  const email_label = document.createElement('label');
  email_label.className = 'mr_modal_label';
  email_label.htmlFor = email_input.id;
  email_label.textContent = 'Email *';

  const name_input = document.createElement('input');
  name_input.type = 'text';
  name_input.placeholder = 'Name (optional)';
  name_input.id = create_widget_control_id('mr_modal_name');
  const name_label = document.createElement('label');
  name_label.className = 'mr_modal_label';
  name_label.htmlFor = name_input.id;
  name_label.textContent = 'Name (optional)';

  const status_el = document.createElement('div');
  status_el.className = 'mr_modal_status';
  status_el.setAttribute('role', 'status');
  status_el.setAttribute('aria-live', 'polite');
  status_el.setAttribute('aria-atomic', 'true');
  status_el.id = create_widget_control_id('mr_modal_status');
  email_input.setAttribute('aria-describedby', status_el.id);

  const actions = document.createElement('div');
  actions.className = 'mr_modal_actions';

  const cancel_button = document.createElement('button');
  cancel_button.type = 'button';
  cancel_button.textContent = 'Cancel';
  cancel_button.className = 'mr_modal_button mr_modal_button_secondary';

  const submit_button = document.createElement('button');
  submit_button.type = 'button';
  submit_button.textContent = 'Get Code';
  submit_button.className = 'mr_modal_button';

  cancel_button.addEventListener('click', close_modal);

  submit_button.addEventListener('click', async () => {
    const email = email_input.value.trim();
    if (!email) {
      status_el.textContent = 'Please enter your email address.';
      status_el.className = 'mr_modal_status error';
      email_input.focus();
      return;
    }

    submit_button.disabled = true;
    submit_button.textContent = 'Submitting...';
    status_el.textContent = '';
    status_el.className = 'mr_modal_status';

    const state = ensure_widget_state();
    const payload: any = {
      business_id: config.business_id,
      embed_code_id: config.embed_code_id,
      visitor_id: state.visitor_id,
      page_url: window.location.href,
      referral: {
        email,
        name: name_input.value.trim() || undefined,
      },
    };

    try {
      const response = await post_json(`${config.api_base_url}/v1/referrals/submit`, payload);

      if (response?.ok) {
        if (response.confirmation_token) set_confirmation_token(response.confirmation_token);
        if (response.referral_code) set_referral_share_code(response.referral_code);

        // show success state in modal
        modal.innerHTML = '';
        const success_div = document.createElement('div');
        success_div.className = 'mr_modal_success';

        const success_title = document.createElement('h2');
        success_title.className = 'mr_modal_title';
        success_title.id = title.id;
        success_title.tabIndex = -1;
        overlay.setAttribute('aria-labelledby', success_title.id);
        success_title.textContent = response.is_existing_referral
          ? 'You already have a referral code!'
          : 'Your referral code';

        success_div.appendChild(success_title);

        if (response.referral_code) {
          const code_el = document.createElement('div');
          code_el.className = 'mr_modal_success_code';
          code_el.textContent = response.referral_code;
          success_div.appendChild(code_el);
        }

        const email_msg = document.createElement('p');
        email_msg.className = 'mr_modal_copy';
        if (response.is_existing_referral) {
          email_msg.textContent = response.email_sent
            ? `This code was already created for ${email}. We've sent a reminder to your email.`
            : `This code was already created for ${email}.`;
        } else if (response.email_sent) {
          email_msg.textContent = `We've also sent this code to ${email}.`;
        } else {
          email_msg.textContent = 'Share this code with friends to earn rewards!';
        }
        success_div.appendChild(email_msg);

        // show rewards if available
        const rewards = response.rewards as embed_rewards | undefined;
        if (rewards && (rewards.new_client_reward || rewards.existing_client_reward)) {
          const rewards_div = document.createElement('div');
          rewards_div.className = 'mr_modal_rewards';
          if (rewards.new_client_reward) {
            const p = document.createElement('p');
            p.textContent = `New client gets: ${format_reward(rewards.new_client_reward)}`;
            rewards_div.appendChild(p);
          }
          if (rewards.existing_client_reward) {
            const p = document.createElement('p');
            p.textContent = `You get: ${format_reward(rewards.existing_client_reward)}`;
            rewards_div.appendChild(p);
          }
          success_div.appendChild(rewards_div);
        }

        // show requirements if available
        const requirements_text = response.requirements?.trim();
        if (requirements_text) {
          const requirements_div = document.createElement('div');
          requirements_div.className = 'mr_modal_requirements';

          const requirements_label = document.createElement('div');
          requirements_label.className = 'mr_modal_requirements_label';
          requirements_label.textContent = 'Please note';
          requirements_div.appendChild(requirements_label);
          const requirements_content = document.createElement('div');
          requirements_content.textContent = requirements_text;
          requirements_div.appendChild(requirements_content);
          success_div.appendChild(requirements_div);
        }

        modal.appendChild(success_div);

        const close_actions = document.createElement('div');
        close_actions.className = 'mr_modal_actions mr_modal_actions_center';
        const close_btn = document.createElement('button');
        close_btn.type = 'button';
        close_btn.textContent = 'Done';
        close_btn.className = 'mr_modal_button';
        close_btn.addEventListener('click', close_modal);
        close_actions.appendChild(close_btn);
        modal.appendChild(close_actions);
        if (overlay.open) {
          overlay.scrollTop = 0;
          success_title.focus({ preventScroll: true });
        }

      } else {
        status_el.textContent = response?.error === 'referral_email_or_phone_required'
          ? 'Please enter a valid email address.'
          : 'Something went wrong. Please try again.';
        status_el.className = 'mr_modal_status error';
        submit_button.disabled = false;
        submit_button.textContent = 'Get Code';
      }
    } catch {
      status_el.textContent = 'Network error. Please try again.';
      status_el.className = 'mr_modal_status error';
      submit_button.disabled = false;
      submit_button.textContent = 'Get Code';
    }
  });

  actions.appendChild(cancel_button);
  actions.appendChild(submit_button);

  modal.appendChild(title);
  modal.appendChild(email_label);
  modal.appendChild(email_input);
  modal.appendChild(name_label);
  modal.appendChild(name_input);
  modal.appendChild(status_el);
  modal.appendChild(actions);

  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  overlay.addEventListener('close', () => overlay.remove(), { once: true });
  let pointer_started_outside = false;
  const is_outside = (event: MouseEvent) => {
    const rect = overlay.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
  };
  overlay.addEventListener('pointerdown', (event) => { pointer_started_outside = is_outside(event); });
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay && pointer_started_outside && is_outside(event)) close_modal();
    pointer_started_outside = false;
  });
  overlay.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const controls = Array.from(overlay.querySelectorAll<HTMLElement>('input:not([disabled]), button:not([disabled])')).filter(control => control.getClientRects().length > 0);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  });
  overlay.showModal();
  email_input.focus();
}

function create_widget_control_id(prefix: string): string {
  let suffix = 0;
  let control_id = prefix;
  while (document.getElementById(control_id)) control_id = prefix + '_' + ++suffix;
  return control_id;
}

function inject_referral_input(form: HTMLFormElement, config: widget_config) {
  if (form.querySelector('.mr_widget_container')) return;

  inject_styles();

  const insert_before = find_insert_before_element(form, config);
  const container = create_container();

  const label = document.createElement('label');
  label.className = 'mr_widget_label';
  label.textContent = 'Referral Code';

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'mr_widget_input';
  input.id = create_widget_control_id('mr_referral_code_input');
  label.htmlFor = input.id;
  input.name = 'mr_referral_code_input';
  input.placeholder = 'Enter referral code';

  const hidden_input = document.createElement('input');
  hidden_input.type = 'hidden';
  hidden_input.name = 'mr_referral_code';

  const status = document.createElement('div');
  status.className = 'mr_widget_status';
  status.id = create_widget_control_id('mr_referral_code_status');
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  status.setAttribute('aria-atomic', 'true');
  input.setAttribute('aria-describedby', status.id);

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'mr_widget_button';
  button.textContent = 'Get your referral code';

  const state = ensure_widget_state();
  const prefill = get_query_param('ref') || state.referral_code;
  if (prefill) {
    input.value = prefill;
    hidden_input.value = prefill;
    set_referral_code(prefill);
  }

  input.addEventListener('input', () => {
    const value = input.value.trim();
    input.removeAttribute('aria-invalid');
    hidden_input.value = value;
    set_referral_code(value || undefined);
  });

  if (config.validate_on_blur) {
    input.addEventListener('blur', () => {
      const code = input.value.trim();
      if (!code) return;
      validate_referral_code(code, config, status);
    });
  }

  button.addEventListener('click', () => {
    // prefill with the form's email field, not the referral code input
    const form_email_el = form.querySelector<HTMLInputElement>('input[type="email"]');
    const form_email = form_email_el?.value?.trim() || '';
    create_modal(form_email, config);
  });

  // rewards display area
  const rewards_el = document.createElement('div');
  rewards_el.className = 'mr_widget_rewards';
  rewards_el.style.display = 'none';

  container.appendChild(label);
  container.appendChild(input);
  container.appendChild(hidden_input);
  container.appendChild(status);
  container.appendChild(button);
  container.appendChild(rewards_el);
  const branding = document.createElement('a');
  // Every embedded widget is an outbound link; UTM params let Plausible/Umami attribute signups that start from a customer site.
  branding.href = `https://monkeyreferrals.com/?utm_source=widget&utm_medium=referral&utm_campaign=powered_by&utm_content=${encodeURIComponent(config.business_id)}`;
  branding.target = '_blank'; branding.rel = 'noopener';
  branding.textContent = 'Powered by Monkey Referrals';
  branding.style.cssText = 'display:block;margin-top:12px;font:12px system-ui;color:inherit;opacity:.75';
  const config_url = `${config.api_base_url}/v1/embed_config?business_id=${encodeURIComponent(config.business_id)}&embed_code_id=${encodeURIComponent(config.embed_code_id)}`;
  const config_result = get_json(config_url);
  void config_result.then(result => { if (!result?.ok || result.show_branding !== false) container.append(branding); }).catch(() => container.append(branding));

  if (insert_before && insert_before.parentElement) {
    insert_before.parentElement.insertBefore(container, insert_before);
  } else {
    form.appendChild(container);
  }

  // fetch and display rewards (best-effort, non-blocking)
  void fetch_rewards(config, config_result).then((rewards) => {
    if (!rewards) return;
    if (rewards.new_client_reward) {
      const reward_line = document.createElement('p');
      reward_line.textContent = 'New client gets: ' + format_reward(rewards.new_client_reward);
      rewards_el.appendChild(reward_line);
    }
    if (rewards.existing_client_reward) {
      const reward_line = document.createElement('p');
      reward_line.textContent = 'Referrer gets: ' + format_reward(rewards.existing_client_reward);
      rewards_el.appendChild(reward_line);
    }
    if (rewards_el.childElementCount) rewards_el.style.display = 'block';
  });
}

async function validate_referral_code(
  code: string,
  config: widget_config,
  status_el: HTMLElement
) {
  const input = status_el.closest('.mr_widget_container')?.querySelector<HTMLInputElement>('.mr_widget_input');
  input?.removeAttribute('aria-invalid');
  status_el.textContent = 'Validating...';
  status_el.classList.remove('success', 'error');

  try {
    const response = await post_json(`${config.api_base_url}/v1/referrals/validate`, {
      business_id: config.business_id,
      embed_code_id: config.embed_code_id,
      referral_code: code,
    });

    if (response.ok) {
      status_el.textContent = 'Code accepted';
      status_el.classList.add('success');
      widget_state = ensure_widget_state();
      widget_state.referral_token = response.referral_token;
      set_referral_code(code);
    } else {
      input?.setAttribute('aria-invalid', 'true');
      status_el.textContent = 'Invalid code';
      status_el.classList.add('error');
      widget_state = ensure_widget_state();
      widget_state.referral_token = undefined;
    }
  } catch {
    status_el.textContent = 'Validation failed';
    status_el.classList.add('error');
  }
}

function extract_referral_data(form: HTMLFormElement) {
  const email_input = form.querySelector<HTMLInputElement>('input[type="email"]');
  const phone_input =
    form.querySelector<HTMLInputElement>('input[type="tel"]') ||
    form.querySelector<HTMLInputElement>('input[name*="phone"], input[id*="phone"]');
  const name_input =
    form.querySelector<HTMLInputElement>('input[name*="name"], input[id*="name"]');

  return {
    email: email_input?.value?.trim() || undefined,
    phone: phone_input?.value?.trim() || undefined,
    name: name_input?.value?.trim() || undefined,
  };
}

function bind_form_submit(form: HTMLFormElement, config: widget_config) {
  if (form.dataset.mrBound === '1') return;
  form.dataset.mrBound = '1';

  let mr_submitting = false;

  form.addEventListener('submit', (e) => {
    // if we're re-submitting after API call, let it through
    if (mr_submitting) return;

    if (form.checkValidity && !form.checkValidity()) {
      if (form.reportValidity) form.reportValidity();
      return;
    }

    const referral = extract_referral_data(form);
    if (!referral.email && !referral.phone) return;

    // prevent default navigation so we can await the API
    e.preventDefault();

    const state = ensure_widget_state();
    const payload: any = {
      business_id: config.business_id,
      embed_code_id: config.embed_code_id,
      visitor_id: state.visitor_id,
      page_url: window.location.href,
      referral,
    };

    if (state.referral_token) {
      payload.referral_token = state.referral_token;
    } else if (state.referral_code) {
      payload.referral_code = state.referral_code;
    }

    const continue_submit = () => {
      mr_submitting = true;
      // use requestSubmit if available (triggers submit event), else submit()
      if (form.requestSubmit) {
        form.requestSubmit();
      } else {
        form.submit();
      }
    };

    // set a safety timeout so the form always submits even if API is slow
    const safety_timeout = setTimeout(continue_submit, 5000);

    void post_json(`${config.api_base_url}/v1/referrals/submit`, payload, {
      keepalive: true,
      allow_beacon: true,
      retry: true,
      storage_key: storage_keys.pending_submit,
    }).then((response) => {
      clearTimeout(safety_timeout);
      if (response?.ok) {
        if (response.confirmation_token) set_confirmation_token(response.confirmation_token);
        if (response.referral_code) set_referral_share_code(response.referral_code);
      }
      continue_submit();
    }).catch(() => {
      clearTimeout(safety_timeout);
      continue_submit();
    });
  });
}

type request_options = {
  keepalive?: boolean;
  allow_beacon?: boolean;
  retry?: boolean;
  storage_key?: string;
};

interface pending_request {
  url: string;
  data: any;
  created_at: number;
  retry_count: number;
}

function store_pending_request(storage_key: string, url: string, data: any) {
  try {
    const pending: pending_request = {
      url,
      data,
      created_at: Date.now(),
      retry_count: 0,
    };
    localStorage.setItem(storage_key, JSON.stringify(pending));
  } catch {
    // ignore storage errors
  }
}

function get_pending_request(storage_key: string): pending_request | null {
  try {
    const stored = localStorage.getItem(storage_key);
    if (!stored) return null;
    return JSON.parse(stored) as pending_request;
  } catch {
    return null;
  }
}

function clear_pending_request(storage_key: string) {
  try {
    localStorage.removeItem(storage_key);
  } catch {
    // ignore
  }
}

function calculate_backoff_delay(retry_count: number): number {
  const delay = retry_config.base_delay_ms * Math.pow(2, retry_count);
  return Math.min(delay, retry_config.max_delay_ms);
}

async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function post_json_with_retry(
  url: string,
  data: any,
  options: request_options = {}
): Promise<any> {
  const keepalive = options.keepalive === true;
  const allow_beacon = options.allow_beacon === true;
  const retry = options.retry === true;
  const storage_key = options.storage_key;

  let last_error: any = null;
  
  for (let attempt = 0; attempt <= retry_config.max_retries; attempt++) {
    if (attempt > 0) {
      await sleep(calculate_backoff_delay(attempt - 1));
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        keepalive,
      });

      const result = await response.json();
      
      // clear pending on success
      if (result?.ok && storage_key) {
        clear_pending_request(storage_key);
      }
      
      return result;
    } catch (error) {
      last_error = error;
      
      // store for retry on next page load if this is the last attempt
      if (retry && storage_key && attempt === retry_config.max_retries) {
        store_pending_request(storage_key, url, data);
      }
    }
  }

  // fallback to beacon if all retries failed
  if (allow_beacon && navigator.sendBeacon) {
    const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    const beacon_sent = navigator.sendBeacon(url, blob);
    if (beacon_sent) {
      if (storage_key) clear_pending_request(storage_key);
      return { ok: true, beacon: true };
    }
  }

  return { ok: false, error: 'network_error' };
}

async function post_json(url: string, data: any, options: request_options = {}): Promise<any> {
  return post_json_with_retry(url, data, options);
}

async function get_json(url: string): Promise<any> {
  try {
    const response = await fetch(url);
    return await response.json();
  } catch {
    return { ok: false, error: 'network_error' };
  }
}

async function fetch_rewards(config: widget_config, pending?: Promise<any>): Promise<embed_rewards | undefined> {
  const state = ensure_widget_state();
  if (state.rewards) return state.rewards;
  try {
    const url = `${config.api_base_url}/v1/embed_config?business_id=${encodeURIComponent(config.business_id)}&embed_code_id=${encodeURIComponent(config.embed_code_id)}`;
    const result = await (pending || get_json(url));
    if (result?.ok && result.rewards) {
      state.rewards = result.rewards;
      return result.rewards;
    }
  } catch {
    // ignore - rewards display is best-effort
  }
  return undefined;
}

async function retry_pending_requests() {
  // retry pending submit
  const pending_submit = get_pending_request(storage_keys.pending_submit);
  if (pending_submit && pending_submit.retry_count < retry_config.max_retries) {
    const result = await post_json_with_retry(
      pending_submit.url,
      pending_submit.data,
      { retry: false, storage_key: storage_keys.pending_submit }
    );
    if (result?.ok) {
      if (result.confirmation_token) set_confirmation_token(result.confirmation_token);
      if (result.referral_code) set_referral_share_code(result.referral_code);
    }
  }

  // retry pending confirm
  const pending_confirm = get_pending_request(storage_keys.pending_confirm);
  if (pending_confirm && pending_confirm.retry_count < retry_config.max_retries) {
    const result = await post_json_with_retry(
      pending_confirm.url,
      pending_confirm.data,
      { retry: false, storage_key: storage_keys.pending_confirm }
    );
    if (result?.ok) {
      set_confirmation_token(undefined);
    }
  }
}

function bind_widget(config: widget_config) {
  const form = find_target_form(config);
  if (!form) return;
  inject_referral_input(form, config);
  bind_form_submit(form, config);
}

function capture_referral_from_url() {
  const ref = get_query_param('ref');
  if (ref) {
    set_referral_code(ref);
  }
}

function init_widget(config_override?: Partial<widget_config>) {
  const script_el = get_current_script();
  if (!script_el) return;

  const config = get_script_config(script_el);
  if (!config) return;

  active_config = { ...config, ...config_override } as widget_config;
  ensure_widget_state();
  capture_referral_from_url();

  bind_widget(active_config);

  if (!mutation_observer) {
    mutation_observer = new MutationObserver(() => {
      if (bind_timeout_id) return;
      bind_timeout_id = window.setTimeout(() => {
        bind_timeout_id = null;
        if (active_config) bind_widget(active_config);
      }, 250);
    });

    mutation_observer.observe(document.body, { childList: true, subtree: true });
  }
}

function confirm_referral() {
  const state = ensure_widget_state();
  if (!active_config || !state.confirmation_token) return;

  void post_json(
    `${active_config.api_base_url}/v1/referrals/confirm`,
    { confirmation_token: state.confirmation_token },
    { 
      allow_beacon: true, 
      keepalive: true,
      retry: true,
      storage_key: storage_keys.pending_confirm,
    }
  ).then((response) => {
    if (response?.ok) {
      set_confirmation_token(undefined);
    }
  });
}

function handle_command(command: string, args: any[]) {
  if (command === 'init') {
    init_widget(args[0] || {});
    return;
  }
  if (command === 'confirm') {
    confirm_referral();
    return;
  }
  // backward compatibility with old 'finalize' command
  if (command === 'finalize') {
    confirm_referral();
    return;
  }
}

function attach_global_mr() {
  const existing = (window as any).mr;
  const queued: mr_command[] = [];

  if (existing) {
    if (Array.isArray(existing.q)) {
      for (const item of existing.q) {
        queued.push({ command: item[0], args: Array.prototype.slice.call(item, 1) });
      }
    } else if (Array.isArray(existing)) {
      for (const item of existing) {
        queued.push({ command: item[0], args: Array.prototype.slice.call(item, 1) });
      }
    }
  }

  const mr = (...args: any[]) => {
    const [command, ...rest] = args;
    handle_command(command, rest);
  };

  (mr as any).q = queued.map((item) => [item.command, ...item.args]);
  (window as any).mr = mr;

  for (const item of queued) {
    handle_command(item.command, item.args);
  }
}

function init_on_dom_ready() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init_widget();
      void retry_pending_requests();
    });
  } else {
    init_widget();
    void retry_pending_requests();
  }
}

attach_global_mr();
init_on_dom_ready();
